/**
 * Chat Input Component
 *
 * Text input with send button for composing messages.
 * Supports RTL and disabled states.
 */

import { useRef, useState, KeyboardEvent, ChangeEvent } from 'react';
import type { Labels } from '../types/widget';

// Send icon
const SendIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="lc-w-4 lc-h-4"
    >
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);

const LockClockIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="lc-w-5 lc-h-5"
    >
        <rect x="3" y="11" width="18" height="10" rx="2" />
        <path d="M7 11V8a5 5 0 0 1 10 0v3" />
        <circle cx="12" cy="16" r="0.5" />
        <path d="M12 16v2" />
    </svg>
);

interface ChatInputProps {
    onSend: (message: string) => void;
    disabled?: boolean;
    inputDisabled?: boolean;
    labels: Labels;
    primaryColor?: string;
    isBlocked?: boolean;
    blockedMessage?: string | null;
    remainingBlockedSeconds?: number;
    chatMode?: 'bot' | 'agent';
}

const MAX_CHARS = 500;

function formatCountdown(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function ChatInput({
                              onSend,
                              disabled,
                              inputDisabled,
                              labels,
                              primaryColor = '#FF3E6C',
                              isBlocked = false,
                              blockedMessage = null,
                              remainingBlockedSeconds = 0,
                              chatMode = 'bot',
                          }: ChatInputProps) {
    const [value, setValue] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const currentLength = value.length;
    const remainingChars = MAX_CHARS - currentLength;

    const resizeTextarea = (element: HTMLTextAreaElement) => {
        element.style.height = 'auto';
        element.style.height = `${Math.min(element.scrollHeight, 96)}px`;
    };

    const resetTextareaHeight = () => {
        if (!textareaRef.current) return;
        textareaRef.current.style.height = '42px';
    };

    const handleSend = () => {
        const text = value.trim();
        if (!text || disabled || inputDisabled || value.length > MAX_CHARS) return;

        onSend(text);
        setValue('');
        resetTextareaHeight();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const nextValue = e.target.value.slice(0, MAX_CHARS);
        setValue(nextValue);

        if (textareaRef.current) {
            textareaRef.current.value = nextValue;
            resizeTextarea(textareaRef.current);
        }
    };

    const canSend =
        value.trim().length > 0 &&
        !disabled &&
        !inputDisabled &&
        value.length <= MAX_CHARS;

    const counterColor =
        remainingChars <= 0
            ? '#EF4444'
            : remainingChars <= 50
                ? '#F59E0B'
                : '#9CA3AF';

    return (
        <div className="lc-px-3 lc-pt-2 lc-pb-3 lc-bg-white lc-border-t lc-border-gray-200">
            {chatMode === 'bot' && isBlocked ? (
                <>
                    <div
                        className="lc-rounded-[22px] lc-border lc-bg-white lc-px-4 lc-py-3"
                        style={{
                            borderColor: `${primaryColor}30`,
                            boxShadow:
                                '0 -6px 16px rgba(15, 23, 42, 0.04), 0 14px 30px rgba(15, 23, 42, 0.07), 0 3px 10px rgba(15, 23, 42, 0.05)',
                        }}
                    >
                        <div className="lc-flex lc-items-center lc-gap-3">
                            <div
                                className="lc-w-10 lc-h-10 lc-rounded-full lc-flex lc-items-center lc-justify-center lc-flex-shrink-0"
                                style={{ backgroundColor: `${primaryColor}14`, color: primaryColor }}
                            >
                                <LockClockIcon />
                            </div>

                            <div className="lc-flex-1 lc-text-right">
                                <div className="lc-text-sm lc-font-semibold lc-text-gray-800">
                                    חסימת הודעות זמנית
                                </div>
                                <div className="lc-text-[12px] lc-text-gray-500 lc-mt-1">
                                    {blockedMessage || 'הגעת למגבלת ההודעות בזמן המסוים.'}
                                </div>
                            </div>
                        </div>

                        <div
                            className="lc-mt-3 lc-rounded-2xl lc-text-center lc-py-2 lc-text-sm lc-font-bold"
                            style={{
                                backgroundColor: `${primaryColor}10`,
                                color: primaryColor,
                            }}
                        >
                            {formatCountdown(remainingBlockedSeconds)}
                        </div>
                    </div>

                    <div className="lc-text-center lc-mt-2">
                        <a
                            href="https://ai.feed-b.co.il/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lc-inline-flex lc-items-center lc-gap-1 lc-text-[11px] lc-font-medium lc-text-gray-400 hover:lc-text-gray-600 lc-transition-colors"
                        >
                            🚀 Powered by FeedBackAI v1.0
                        </a>
                    </div>
                </>
            ) : (
                <>
                    <div
                        className="lc-rounded-[22px] lc-border lc-border-gray-200 lc-bg-white"
                        style={{
                            boxShadow:
                                '0 -6px 16px rgba(15, 23, 42, 0.04), 0 14px 30px rgba(15, 23, 42, 0.07), 0 3px 10px rgba(15, 23, 42, 0.05)',
                            padding: '8px',
                        }}
                    >
                        <div className="lc-flex lc-items-end lc-gap-2">
              <textarea
                  ref={textareaRef}
                  placeholder={labels.inputPlaceholder}
                  value={value}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  disabled={inputDisabled}
                  rows={1}
                  className="lc-flex-1 lc-bg-transparent lc-border-none lc-text-sm lc-outline-none lc-text-right disabled:lc-opacity-50 disabled:lc-cursor-not-allowed"
                  style={{
                      direction: 'rtl',
                      resize: 'none',
                      overflow: 'hidden',
                      minHeight: '42px',
                      maxHeight: '96px',
                      padding: '10px 12px 8px 12px',
                  }}
              />

                            <button
                                type="button"
                                onClick={handleSend}
                                disabled={!canSend}
                                aria-label={labels.sendButton}
                                className="lc-rounded-full lc-border-none lc-cursor-pointer lc-flex lc-items-center lc-justify-center lc-text-white lc-transition-all disabled:lc-opacity-50 disabled:lc-cursor-not-allowed hover:lc-scale-105 active:lc-scale-95 lc-flex-shrink-0"
                                style={{
                                    backgroundColor: canSend ? primaryColor : '#E5E7EB',
                                    width: '34px',
                                    height: '34px',
                                    minWidth: '34px',
                                    minHeight: '34px',
                                    boxShadow: canSend ? '0 6px 14px rgba(0, 0, 0, 0.10)' : 'none',
                                }}
                            >
                                <SendIcon />
                            </button>
                        </div>

                        <div className="lc-flex lc-items-center lc-justify-end lc-mt-1 lc-px-2">
                            <div
                                className="lc-text-[11px] lc-font-medium"
                                style={{ color: counterColor }}
                            >
                                {currentLength} / {MAX_CHARS}
                            </div>
                        </div>
                    </div>

                    <div className="lc-text-center lc-mt-2">
                        <a
                            href="https://ai.feed-b.co.il/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lc-inline-flex lc-items-center lc-gap-1 lc-text-[11px] lc-font-medium lc-text-gray-400 hover:lc-text-gray-600 lc-transition-colors"
                        >
                            🚀 Powered by FeedBackAI v1.0
                        </a>
                    </div>
                </>
            )}
        </div>
    );
}